
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Info } from 'lucide-react';
import { BlogPostFormData } from '@/hooks/blog/useBlogPostForm';

interface BlogSeoTabProps {
  formData: BlogPostFormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export function BlogSeoTab({
  formData,
  handleChange
}: BlogSeoTabProps) {
  return (
    <Card className="glass-card">
      <CardHeader>
        <CardTitle>SEO Settings</CardTitle>
        <CardDescription>
          Optimize your blog post for search engines
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center">
            <Label htmlFor="seo_title" className="mr-2">SEO Title</Label>
            <div className="text-sm text-muted-foreground flex items-center">
              <Info className="h-4 w-4 mr-1" />
              <span>Recommended: 50-60 characters</span>
            </div>
          </div>
          <Input 
            id="seo_title" 
            name="seo_title" 
            placeholder="Enter SEO title (shown in search results)" 
            value={formData.seo_title}
            onChange={handleChange}
          />
          <p className="text-xs text-muted-foreground">
            Characters: {formData.seo_title.length}/60
          </p>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center">
            <Label htmlFor="seo_description" className="mr-2">Meta Description</Label>
            <div className="text-sm text-muted-foreground flex items-center">
              <Info className="h-4 w-4 mr-1" />
              <span>Recommended: 150-160 characters</span>
            </div>
          </div>
          <Textarea 
            id="seo_description" 
            name="seo_description" 
            placeholder="Brief description for search results" 
            value={formData.seo_description}
            onChange={handleChange}
            className="min-h-[80px]"
          />
          <p className="text-xs text-muted-foreground">
            Characters: {formData.seo_description.length}/160
          </p>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="seo_keywords">Keywords</Label>
          <Input 
            id="seo_keywords" 
            name="seo_keywords" 
            placeholder="Enter keywords separated by commas" 
            value={formData.seo_keywords}
            onChange={handleChange}
          />
          <p className="text-xs text-muted-foreground">
            Example: travel, hiking, mountains, adventure
          </p>
        </div>

        <div className="mt-4 p-4 bg-muted/50 rounded-md">
          <h4 className="font-medium mb-2">Search Preview</h4>
          <div className="space-y-1">
            <p className="text-blue-600 font-medium text-lg break-words">
              {formData.seo_title || formData.title || "Title"}
            </p>
            <p className="text-green-700 text-sm">
              yourdomain.com/blog/{formData.slug || "url-slug"}
            </p>
            <p className="text-gray-600 text-sm break-words">
              {formData.seo_description || formData.excerpt || "Description will appear here..."}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
